/**
 * GLOBAL MODAL STORE
 *
 * Controla quando modais/janelas flutuantes centrais estão abertos.
 * Quando um bloqueador está ativo:
 * - O Smart Inspector DESAPARECE completamente (return null)
 * - A tecla F NÃO restaura o SI
 * - SI retorna automaticamente quando o bloqueador fecha
 *
 * Dois modos de retorno:
 * - closeOnReturn: true  → SI volta COLAPSADO (ex: Preferências)
 * - closeOnReturn: false → SI volta ao estado anterior (ex: janelas flutuantes)
 *
 * Usado por: Todos os Smart Inspectors (8) + useFocusMode
 */

import { create } from 'zustand';

interface GlobalModalState {
  // Modal/bloqueador ativo (ou null se nenhum)
  activeGlobalModal: string | null;

  // Flag para forçar SI a fechar quando bloqueador fecha
  shouldCloseInspectorOnReturn: boolean;

  // Abrir um bloqueador (oculta SI)
  openGlobalModal: (modalId: string, options?: { closeOnReturn?: boolean }) => void;

  // Fechar bloqueador (SI retorna)
  closeGlobalModal: () => void;

  // Resetar flag após SI processar
  resetInspectorCloseFlag: () => void;
}

export const useGlobalModalStore = create<GlobalModalState>((set, _get) => ({
  activeGlobalModal: null,
  shouldCloseInspectorOnReturn: false,

  openGlobalModal: (modalId: string, options?: { closeOnReturn?: boolean }) => {
    set({
      activeGlobalModal: modalId,
      // Por padrão SI volta ao estado anterior (closeOnReturn: false)
      // Preferências usa closeOnReturn: true para forçar SI fechado
      shouldCloseInspectorOnReturn: options?.closeOnReturn ?? false,
    });
  },

  closeGlobalModal: () => {
    set({ activeGlobalModal: null });
  },

  resetInspectorCloseFlag: () => {
    set({ shouldCloseInspectorOnReturn: false });
  },
}));

/**
 * Hook para verificar se o Smart Inspector deve ser ocultado
 * Use em qualquer Inspector:
 * 
 * const shouldHide = useShouldHideInspector();
 * if (shouldHide) return null;
 */
export const useShouldHideInspector = () => {
  const activeGlobalModal = useGlobalModalStore(state => state.activeGlobalModal);
  return activeGlobalModal !== null;
};

/**
 * Hook para verificar se SI deve fechar ao retornar
 * Use para fechar o SI quando modal global fecha:
 * 
 * const { shouldClose, resetFlag } = useShouldCloseInspector();
 * useEffect(() => {
 *   if (shouldClose && !shouldHide) {
 *     setIsOpen(false);
 *     resetFlag();
 *   }
 * }, [shouldClose, shouldHide]);
 */
export const useShouldCloseInspector = () => {
  const shouldClose = useGlobalModalStore(state => state.shouldCloseInspectorOnReturn);
  const resetFlag = useGlobalModalStore(state => state.resetInspectorCloseFlag);
  return { shouldClose, resetFlag };
};
